import React, { useCallback, useEffect, useRef, useState } from "react";

// MUI
import {
    Box,
    Chip,
    Stack,
    TextField,
    Typography,
    Divider,
} from "@mui/material";

import { ds } from "../../../../../styles/designSystem";
import { keyVisualApi } from "../../api/keyVisualApi";
import AppAutocomplete from "../../../../../component/common/AppAutocomplete";
import FormModal from "../../../../../component/common/FormModal";
import AssetPicker, { assetDisplayName } from "../AssetPicker";
import { confirm as confirmDialog } from "../../../../../util/confirmDialog";

export default function EditKeyVisualModal({ open, keyVisual, onClose, onSaved, openSnack }) {
    const itemId = keyVisual?.id;
    const initialName = keyVisual?.name || "";
    const initialKeywords = keyVisual?.keywords || [];
    const initialProjectName = keyVisual?.projectName || "";

    const [name, setName] = useState(initialName);
    const [projectName, setProjectName] = useState(initialProjectName);
    const [projectOptions, setProjectOptions] = useState([]);
    const [keywords, setKeywords] = useState([]);
    const [newKeyword, setNewKeyword] = useState("");
    const [saving, setSaving] = useState(false);

    /**
     * 로고·레퍼런스는 <b>'저장' 버튼과 무관하게 즉시</b> 서버에 반영된다.
     *
     * <p>파일은 텍스트 필드처럼 폼 상태로 들고 있다가 한꺼번에 커밋할 수 없다(멀티파트라
     * editKeyVisual 의 JSON 페이로드에 섞이지 않는다). 그래서 고르는 즉시 업로드하고,
     * 화면에는 서버가 돌려준 최신 목록을 그대로 반영한다.
     * <b>취소를 눌러도 첨부는 되돌아가지 않는다</b> — 그래서 아래 안내 문구를 달아 둔다.
     */
    const [logoUrls, setLogoUrls] = useState([]);
    const [referenceUrls, setReferenceUrls] = useState([]);
    const [assetBusy, setAssetBusy] = useState(false);

    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => (mountedRef.current = false);
    }, []);

    // 열릴 때마다 최신 값으로 초기화
    useEffect(() => {
        if (!open) return;
        setName(initialName);
        setProjectName(initialProjectName);
        setKeywords(Array.isArray(initialKeywords) ? initialKeywords : []);
        setNewKeyword("");
        setSaving(false);
        setLogoUrls(Array.isArray(keyVisual?.logoPaths) ? keyVisual.logoPaths : []);
        setReferenceUrls(Array.isArray(keyVisual?.referencePaths) ? keyVisual.referencePaths : []);
        setAssetBusy(false);
    }, [open, itemId]);

    // 자동완성 옵션 — 2D/3D 통합 프로젝트명 목록
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const data = await keyVisualApi.listProjectNames();
                setProjectOptions(Array.isArray(data) ? data : (data?.data || []));
            } catch (e) {
                console.error(e);
            }
        })();
    }, [open]);

    const normalizeParts = useCallback((text) => {
        return String(text || "")
            .trim()
            .replace(/,+$/, "")
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean);
    }, []);

    const addKeywordsFromText = useCallback(
        (text) => {
            const parts = normalizeParts(text);
            if (parts.length === 0) return;

            setKeywords((prev) => {
                const next = [...(prev || [])];
                const set = new Set(next.map((k) => String(k).trim()).filter(Boolean));
                for (const p of parts) {
                    if (!set.has(p)) {
                        next.push(p);
                        set.add(p);
                    }
                }
                return next;
            });
        },
        [normalizeParts]
    );

    const removeKeyword = (k) => {
        setKeywords((prev) => {
            const list = prev || [];
            if (list.length <= 1) {
                openSnack?.("키워드는 최소 1개가 필요합니다. 먼저 키워드를 추가한 뒤 삭제해주세요.", "warning");
                return list;
            }
            const target = String(k).trim();
            return list.filter((x) => String(x).trim() !== target);
        });
    };

    const handleKeyDown = (e) => {
        // 한글 조합 중 Enter 는 '조합 확정' 이지 '입력 완료' 가 아니다 —
        // 거르지 않으면 "한글" 을 치고 Enter 했을 때 "한", "한글" 두 개가 등록된다.
        // (KeyVisualUploader.handleManualKeywordKeyDown 주석에 경위가 있다)
        if (e.nativeEvent?.isComposing) return;
        if (e.key === "Enter" && newKeyword.trim()) {
            e.preventDefault();
            addKeywordsFromText(newKeyword);
            setNewKeyword("");
        }
    };

    /** 첨부 추가 — 고른 즉시 업로드하고, 서버가 준 최신 목록으로 화면을 맞춘다. */
    const handleAssetsAdded = useCallback(async (type, files) => {
        if (!itemId || assetBusy || !files?.length) return;
        setAssetBusy(true);
        try {
            const res  = await keyVisualApi.addAssets(itemId, type, files);
            const list = Array.isArray(res) ? res : (res?.data || []);
            if (type === "LOGO") setLogoUrls(list); else setReferenceUrls(list);
            openSnack?.(`${files.length}개를 첨부했습니다.`, "success");
        } catch (err) {
            console.error(err);
            openSnack?.(err?.message || "첨부에 실패했습니다.", "error");
        } finally {
            if (mountedRef.current) setAssetBusy(false);
        }
    }, [itemId, assetBusy, openSnack]);

    /** 첨부 제거 — 서버가 S3 객체까지 지운다. 되돌릴 수 없어 확인을 받는다. */
    const handleAssetRemove = useCallback(async (type, url) => {
        if (!itemId || assetBusy) return;
        const ok = await confirmDialog({
            title: "첨부 삭제",
            message: `'${assetDisplayName(url)}' 을(를) 삭제할까요?\n파일이 완전히 지워지며 되돌릴 수 없습니다.`,
            confirmLabel: "삭제",
            variant: "danger",
        });
        if (!ok) return;
        setAssetBusy(true);
        try {
            const res  = await keyVisualApi.deleteAsset(itemId, type, url);
            const list = Array.isArray(res) ? res : (res?.data || []);
            if (type === "LOGO") setLogoUrls(list); else setReferenceUrls(list);
            openSnack?.("삭제했습니다.", "success");
        } catch (err) {
            console.error(err);
            openSnack?.(err?.message || "삭제에 실패했습니다.", "error");
        } finally {
            if (mountedRef.current) setAssetBusy(false);
        }
    }, [itemId, assetBusy, openSnack]);

    const handleSave = async () => {
        if (saving || !itemId) return;

        const trimmedName = String(name || "").trim();
        if (!trimmedName) {
            openSnack?.("키비주얼명은 필수입니다.", "warning");
            return;
        }

        const trimmedProject = String(projectName || "").trim();
        if (!trimmedProject) {
            openSnack?.("프로젝트명은 필수입니다.", "warning");
            return;
        }

        const payloadKeywords = (keywords || []).map((k) => String(k).trim()).filter(Boolean);
        if (payloadKeywords.length < 1) {
            openSnack?.("키워드는 최소 1개가 필요합니다.", "warning");
            return;
        }

        const payload = { name: trimmedName, projectName: trimmedProject, keywords: payloadKeywords };

        setSaving(true);
        try {
            const serverData = await keyVisualApi.editKeyVisual(itemId, payload);
            const serverItem = serverData && typeof serverData === "object" ? serverData : null;

            const updated = {
                ...(keyVisual || {}),
                ...(serverItem || {}),
                id: serverItem?.id ?? itemId,
                name: serverItem?.name ?? trimmedName,
                projectName: serverItem?.projectName ?? trimmedProject,
                keywords: serverItem?.keywords ?? payloadKeywords,
                // 첨부는 저장 버튼과 별개로 이미 반영돼 있다. 여기서 같이 올려보내지 않으면
                // 부모(상세 모달)가 옛 목록을 계속 들고 있어 방금 올린 파일이 안 보인다.
                logoPaths: logoUrls,
                referencePaths: referenceUrls,
            };

            openSnack?.("저장 완료", "success");
            onSaved?.(updated);
        } catch (err) {
            console.error(err);
            // 서버 메시지 (이름 중복 등) 우선 노출
            const msg = err?.response?.data?.message || err?.message || "저장에 실패했습니다.";
            openSnack?.(msg, "error");
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title="키비주얼 수정"
            subtitle="프로젝트명과 키워드를 수정합니다. (키워드 최소 1개 유지)"
            maxWidth="sm"
            onSave={handleSave}
            saving={saving}
        >
            <Stack spacing={1.75}>
                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary", mb: 0.75 }}>
                        <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                        키비주얼명
                    </Typography>
                    <TextField
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="키비주얼명 (중복 불가)"
                        size="small"
                        fullWidth
                        disabled={saving}
                        sx={ds.components.external.inputSoft}
                    />
                </Box>

                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary", mb: 0.75 }}>
                        <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                        프로젝트명
                    </Typography>
                    <AppAutocomplete
                        freeSolo
                        options={projectOptions}
                        value={projectName}
                        inputValue={projectName}
                        onChange={(v) => setProjectName(v == null ? "" : String(v))}
                        onInputChange={(v) => setProjectName(v)}
                        disabled={saving}
                        placeholder="기존 프로젝트 선택 또는 새 이름 입력"
                        variant="outlined" disableUnderline={false}
                        textFieldSx={ds.components.external.inputSoft}
                    />
                </Box>

                <Divider sx={{ my: 0.25 }} />

                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary", mb: 0.75 }}>
                        <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                        키워드
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 12, color: "text.secondary", mb: 0.75 }}>
                        Enter로 추가 / 쉼표로 여러 개 / X로 제거 (최소 1개 유지)
                    </Typography>

                    <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ maxHeight: 140, overflow: "auto", mb: 1 }}
                    >
                        {keywords?.length > 0 ? (
                            keywords.map((k, i) => (
                                <Chip
                                    key={`${String(k)}-${i}`}
                                    label={String(k)}
                                    onDelete={saving ? undefined : () => removeKeyword(k)}
                                    sx={{
                                        ...ds.components.external.chip,
                                        opacity: !saving && keywords.length === 1 ? 0.85 : 1,
                                    }}
                                />
                            ))
                        ) : (
                            <Typography sx={{ fontWeight: 800, color: "text.secondary", fontSize: 13 }}>
                                키워드가 없습니다. 아래 입력창에 추가하세요.
                            </Typography>
                        )}
                    </Stack>

                    <TextField
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="키워드를 입력하고 Enter (여러 개는 ,로 구분)"
                        size="small"
                        fullWidth
                        disabled={saving}
                        sx={ds.components.external.inputSoft}
                    />
                </Box>

                <Divider />

                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary", mb: 0.25 }}>
                        첨부 파일
                    </Typography>
                    {/* 위 항목들과 커밋 시점이 다르다는 걸 분명히 적는다 — '취소' 를 눌렀는데
                        첨부만 남아 있으면 사용자는 버그로 읽는다. */}
                    <Typography sx={{ fontSize: 11.5, color: ds.colors.error, fontWeight: 700, mb: 1 }}>
                        첨부는 고르는 즉시 반영됩니다. ‘취소’를 눌러도 되돌아가지 않습니다.
                    </Typography>

                    <Stack spacing={1.5}>
                        <AssetPicker
                            label="로고 (BI·CI)"
                            urls={logoUrls}
                            onFilesAdded={(files) => handleAssetsAdded("LOGO", files)}
                            onRemoveUrl={(url) => handleAssetRemove("LOGO", url)}
                            disabled={saving || assetBusy}
                        />
                        <AssetPicker
                            label="레퍼런스 이미지"
                            urls={referenceUrls}
                            onFilesAdded={(files) => handleAssetsAdded("REFERENCE", files)}
                            onRemoveUrl={(url) => handleAssetRemove("REFERENCE", url)}
                            disabled={saving || assetBusy}
                        />
                    </Stack>
                </Box>
            </Stack>
        </FormModal>
    );
}
